import {
  CellClassParams,
  ColDef,
  HeaderClassParams,
  ICellRendererParams,
  ITooltipParams,
  ValueGetterParams,
  ProcessCellForExportParams,
} from '@ag-grid-community/core';
import { isColDefPivoted } from './isColDefPivoted';

type GetFieldNameParams =
  | CellClassParams
  | HeaderClassParams
  | ICellRendererParams
  | ITooltipParams
  | ValueGetterParams
  | ProcessCellForExportParams;

/**
 * Figure out what the current SoQL `fieldname` is. AgGrid has lots of different contexts where the fieldname
 * of the actual column in the database gets somewhat obfuscated.
 * This function will find it, regardless of context.
 * @param params Basically any AgGrid callback Params interface.
 * @returns usually string, undefined if we really couldn't find any fieldname.
 */
export function getFieldName(params: GetFieldNameParams): string | undefined {
  const colDef = getColDefFromParams(params);

  if (isColDefPivoted(colDef)) {
    return colDef.context.pivotValueColumn;
  }

  if (hasField(colDef)) {
    return colDef.field;
  }

  if (hasShowRowGroup(colDef)) {
    return colDef.showRowGroup;
  }

  // Use the first key in the data object. Occurs with autoGroupedRows generated by AgGrid.
  // node.field often has the same value, but not always.
  // data: {someAggregatedColumnName: 493}
  if (hasData(params)) {
    return Object.keys(params.data)[0];
  }

  // Occurs with blank cells in grouped rows. See EN-63840
  // These edge cases may no longer exist, but keeping for safety.
  if (hasNode(params)) {
    return params.node.field;
  }

  return undefined;
}

function getColDefFromParams(params: GetFieldNameParams): ColDef | undefined {
  if (hasColDef(params)) {
    return params.colDef;
  }

  if (hasColumn(params)) {
    return params.column.getColDef();
  }

  return undefined;
}

// LOTS of type guards to make the actual functions way more readable and typescript friendly.
// https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates

function hasColDef(params: GetFieldNameParams): params is GetFieldNameParams & { colDef: ColDef } {
  return 'colDef' in params && !!params.colDef;
}

function hasColumn(
  params: GetFieldNameParams,
): params is GetFieldNameParams & { column: { getColDef(): ColDef } } {
  return 'column' in params && !!params.column && 'getColDef' in params.column;
}

function hasData(
  params: GetFieldNameParams,
): params is GetFieldNameParams & { data: Record<string, unknown> } {
  return 'data' in params && !!params.data && Object.keys(params.data).length > 0;
}

function hasNode(params: GetFieldNameParams): params is GetFieldNameParams & { node: { field: string } } {
  return 'node' in params && typeof params.node?.field === 'string';
}

function hasField(colDef: ColDef | undefined): colDef is ColDef & { field: string } {
  return !!colDef && 'field' in colDef && typeof colDef.field === 'string';
}

function hasShowRowGroup(colDef: ColDef | undefined): colDef is ColDef & { showRowGroup: string } {
  return !!colDef && 'showRowGroup' in colDef && typeof colDef.showRowGroup === 'string';
}
